import { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { Button, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import {
  PLAN_CODE_MONTHLY,
  PLAN_CODE_YEARLY,
  SUCCESS_OPERATION_ACTION_TYPE_SUBSCRIPTION_CHANGE,
} from 'constants/payment';
import { DiscountInput, Info, SeatsCountSelector, Spinner } from 'components';
import { IApiRequests, useApiRequests } from 'hooks';
import {
  ApiPlan,
  ComputeSubscriptionProrationMode,
  ComputeSubscriptionProrationResponse,
  Coupon,
  CreateHostedPageResponse,
  GetPlanResponse,
  SubscriptionProrationApi,
} from 'types';

import styles from './SubscriptionStep.module.scss';

export function SubscriptionStep() {
  const { t } = useTranslation('auth');
  const { enqueueSnackbar } = useSnackbar();
  const { getPlan, createSubscriptionWithHostedPages, computeSubscriptionProration }: IApiRequests = useApiRequests();

  const [loading, setLoading] = useState<boolean>(true);
  const [plans, setPlans] = useState<ApiPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>(PLAN_CODE_MONTHLY);
  const [seatsCount, setSeatsCount] = useState<number>(1);
  const [subscriptionProration, setSubscriptionProration] = useState<SubscriptionProrationApi | null>(null);
  const [coupon, setCoupon] = useState<Coupon | null>(null);

  useEffect(() => {
    Promise.all([getPlan(PLAN_CODE_MONTHLY), getPlan(PLAN_CODE_YEARLY)])
      .then((value: [GetPlanResponse, GetPlanResponse]) => {
        setPlans(value.map((planResponse: GetPlanResponse) => planResponse.plan));
      })
      .catch(() => {
        enqueueSnackbar(t('plansFetchError'), { variant: 'error' });
      })
      .finally(() => setLoading(false));
  }, [getPlan, t, enqueueSnackbar]);

  useEffect(() => {
    if (!plans.length) {
      return;
    }

    setLoading(true);

    computeSubscriptionProration({
      plan: { code: selectedPlan, quantity: seatsCount },
      mode: ComputeSubscriptionProrationMode.ProrateNewSubscription,
      couponCode: coupon?.code,
    })
      .then((response: ComputeSubscriptionProrationResponse) => {
        setSubscriptionProration(response.subscriptionProration);
      })
      .catch((error) => {
        enqueueSnackbar(`${t('calculationErrorPrefix')}${error.message}`, { variant: 'error' });
        setSubscriptionProration(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [seatsCount, coupon, selectedPlan, plans.length, computeSubscriptionProration, t, enqueueSnackbar]);

  const pricePerOneSeatPerMonth: string = useMemo(() => {
    if (selectedPlan === PLAN_CODE_MONTHLY) {
      return plans[0]?.recurringPrice?.toFixed(2) ?? '';
    }

    return ((plans[1]?.recurringPrice ?? 0) / 12).toFixed(2);
  }, [selectedPlan, plans]);

  const calculationsSum: string = useMemo(() => {
    if (selectedPlan === PLAN_CODE_MONTHLY) {
      return ((plans[0]?.recurringPrice ?? 0) * seatsCount).toFixed(2);
    }

    return ((plans[1]?.recurringPrice ?? 0) * seatsCount).toFixed(2);
  }, [selectedPlan, plans, seatsCount]);

  const handleMonthlyClick = (): void => {
    if (selectedPlan !== PLAN_CODE_MONTHLY) {
      setCoupon(null);
      setSelectedPlan(PLAN_CODE_MONTHLY);
    }
  };

  const handleYearlyClick = (): void => {
    if (selectedPlan !== PLAN_CODE_YEARLY) {
      setCoupon(null);
      setSelectedPlan(PLAN_CODE_YEARLY);
    }
  };

  const handleDecreaseSeat = (): void => {
    if (seatsCount > 1) {
      setSeatsCount(seatsCount - 1);
    }
  };

  const handleIncreaseSeat = (): void => {
    setSeatsCount(seatsCount + 1);
  };

  const handleApplyCoupon = (appliedCoupon: Coupon): void => {
    setCoupon(appliedCoupon);
  };

  const handleRemoveCoupon = (): void => {
    setCoupon(null);
  };

  const handlePayClick = (): void => {
    if (coupon?.code === 'LIMITED50' && seatsCount < 10) {
      enqueueSnackbar(t('limited50MinSeatsError'), { variant: 'error' });
      return;
    }

    setLoading(true);

    createSubscriptionWithHostedPages({
      planCode: selectedPlan,
      quantity: seatsCount,
      couponCode: coupon?.code,
      redirectUrl: `${process.env.REACT_APP_PUBLIC_URI}/successPayment?type=${SUCCESS_OPERATION_ACTION_TYPE_SUBSCRIPTION_CHANGE}`,
    })
      .then((response: CreateHostedPageResponse) => {
        window.location.href = response.page.url;
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading) {
    return <Spinner small />;
  }

  return (
    <div className={styles.form}>
      <div className={styles.switcher}>
        <div
          onClick={handleMonthlyClick}
          className={classnames(styles.switcherItem, {
            [styles.switcherItemActive]: selectedPlan === PLAN_CODE_MONTHLY,
          })}
        >
          <Typography variant='body5' component='span'>
            {t('monthly')}
          </Typography>
        </div>

        <div
          onClick={handleYearlyClick}
          className={classnames(styles.switcherItem, {
            [styles.switcherItemActive]: selectedPlan === PLAN_CODE_YEARLY,
          })}
        >
          <Typography variant='body5' component='span'>
            {t('annually')}
          </Typography>
        </div>

        <div className={styles.discount}>
          <Typography variant='body' component='span'>
            {t('off20')}
          </Typography>
        </div>
      </div>

      <div className={classnames(styles.row, styles.pricePerMonth)}>
        <Typography variant='h4' component='h4'>
          {t('monthSeatRate', { price: pricePerOneSeatPerMonth })}
        </Typography>

        <Info text={t('seatsInfo')} tooltipClassName={styles.tooltip} />

        <Typography variant='body2' component='span' color='var(--app-text-muted)'>
          {selectedPlan === PLAN_CODE_MONTHLY ? t('billedMonthly') : t('billedAnnually')}
        </Typography>
      </div>

      <div className={styles.row}>
        <Typography variant='body2' component='span'>
          {t('totalSeats')}
        </Typography>
        <SeatsCountSelector count={seatsCount} onDecrease={handleDecreaseSeat} onIncrease={handleIncreaseSeat} />
      </div>

      <div className={styles.row}>
        <Typography variant='body2' component='span'>
          {t('discountCode')}
        </Typography>

        <DiscountInput onApply={handleApplyCoupon} onRemove={handleRemoveCoupon} plan={selectedPlan} />
      </div>

      <div className={styles.divider}></div>

      <div className={styles.row}>
        <div className={styles.calculationsRow}>
          <Typography variant='body2' component='span' color='var(--app-text-default)'>
            {t('subtotal')}
          </Typography>
          <Typography variant='body2' component='span' color='var(--app-text-default)'>
            {calculationsSum}
          </Typography>
        </div>

        <div className={styles.calculationsRow}>
          <Typography variant='body2' component='span' color='var(--app-text-default)'>
            {t('discount')}
          </Typography>
          <Typography variant='body2' component='span' color='var(--app-text-default)'>
            -${subscriptionProration?.initialDiscount.toFixed(2)}
          </Typography>
        </div>

        <div className={styles.calculationsRow}>
          <Typography variant='body4' component='span'>
            {t('total')}
          </Typography>
          <Typography variant='body4' component='span'>
            ${subscriptionProration?.initialChargeAmount.toFixed(2)}
          </Typography>
        </div>
      </div>

      <Button variant='contained' onClick={handlePayClick}>
        {t('payAmount', { amount: subscriptionProration?.initialChargeAmount.toFixed(2) })}
      </Button>
    </div>
  );
}
